import type { JSX } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { Button } from './ds/Button';
import { isStaleChunkError } from './staleDeploy';

/**
 * What the adviser sees when a route throws instead of rendering.
 *
 * Without this, React Router falls back to its own development screen — the raw
 * message, the minified stack, and "💿 Hey developer 👋" — which is what a real
 * adviser hit in production on 2026-08-08. A stack trace is not a page: it
 * looks like a broken product, and it can echo internal detail back to whoever
 * is sitting next to the adviser, which may include their client.
 *
 * So: a plain explanation, a way out, and the technical detail kept to the dev
 * build only. Deliberately free of any modelling claim — nothing here reassures
 * anyone about their figures, because at this point we cannot.
 */
/** Move focus to the heading as soon as the error panel mounts. */
function focusOnMount(node: HTMLHeadingElement | null): void {
  node?.focus();
}

export function RouteError(): JSX.Element {
  const error = useRouteError();

  // A chunk that 404s is almost always a stale tab after a deploy, not a bug.
  // Offer the reload that actually fixes it rather than a generic apology.
  const stale = isStaleChunkError(error);

  const status = isRouteErrorResponse(error) ? error.status : null;
  const notFound = status === 404;

  // No product name in these strings: this element also covers the firm-branded
  // client report route, where naming the software vendor to a client breaks
  // the white-label promise.
  const heading = stale
    ? 'A newer version is available'
    : notFound
      ? 'That page could not be found'
      : 'This page could not be displayed';

  // On the data promise: bounded, not absolute. Editing autosaves on a short
  // delay, pauses while a field is invalid, and stops entirely after a version
  // conflict — so "nothing is lost" would be a promise the product cannot keep,
  // and the reload button beside it is what would break it.
  const body = stale
    ? 'This tab has been open since an earlier version. Reloading picks up the current one. Anything already saved is unaffected, though you may need to re-enter your most recent changes.'
    : notFound
      ? 'The link may be out of date, or the page may have moved.'
      : 'Anything already saved is unaffected. If you were part-way through editing, you may need to re-enter your most recent changes.';

  return (
    <main className="pc-routeerror" data-testid="route-error">
      <div className="pc-routeerror__panel" role="alert">
        {/*
          Focused on mount so a screen-reader or keyboard user lands on the
          explanation. A live region that already exists at render time is
          announced unreliably, so the focus move — not the role — is what
          actually reports the change.
        */}
        <h1 className="pc-routeerror__title" tabIndex={-1} ref={focusOnMount}>
          {heading}
        </h1>
        <p className="pc-routeerror__body">{body}</p>
        <div className="pc-routeerror__actions">
          <Button
            variant="primary"
            onClick={() => {
              // Always reload on an explicit click, marker or not: the person
              // asked for it, so the loop guard does not apply here.
              window.location.reload();
            }}
          >
            Reload the page
          </Button>
          {/*
            Neutral wording and a public destination on purpose. This is the
            ONLY error element in the app, so it also covers the firm-branded
            client report — and will cover the read-only client portal when it
            lands. "Go to your clients" would address a client as though they
            were the adviser, and pointing at the auth-gated /app could drop a
            portal visitor into a staff login (or, on a shared device with a
            live session, an adviser's client list).
          */}
          <Link className="pc-routeerror__link" to="/">
            Back to the start
          </Link>
        </div>
        {import.meta.env.DEV ? (
          <pre className="pc-routeerror__detail" data-testid="route-error-detail">
            {errorDetail(error)}
          </pre>
        ) : null}
      </div>
    </main>
  );
}

/**
 * Developer-build detail only. Exported for the test that pins the production
 * build showing NONE of this.
 */
export function errorDetail(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return `${error.message}\n${error.stack ?? ''}`;
  return String(error);
}
